import { FC, useMemo, useState } from 'react'
import cn from './index.module.css'
import { RoundedStarFilled, RoundedStarOutlined } from '@/utils/icons'
import { addCourseToFavorite } from '@pages/CoursesCatalog/tabs/MyCoursesTab/utils/api'
import { useAddAlert } from '@/hooks/useAddAlert'
import { declOfNum } from '@/helpers/declOfNum'
import { formateDate } from '@/helpers/date'
import classNames from 'classnames'
import { selectProfile } from '@store/profile.reducer'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import ModalBody from '@pages/Payment/modalBody'
import Modal from '@common/Modal'
import TooltipWrapper from '@pages/Admin/CourseSettings/components/TooltipWrapper/TooltipWrapper'
import { useGetSettings } from '@pages/CoursesCatalog/tabs/CatalogCoursesTab/hooks/getPlatformSettings'

interface IMyCourseCard {
  order: ICourseOrder
  onAddToFavorite?: (order: ICourseOrder) => void
}

type ModalType = 'group' | 'private' | 'buy'

type ModalConfig = {
  type: ModalType
  data: ICourseOrder
}

const MyCourseCard: FC<IMyCourseCard> = ({ order, onAddToFavorite }) => {
  const addAlert = useAddAlert()
  const { checkSettings } = useGetSettings()
  const isDisabledExtensionTraining = checkSettings(
    'isDisableExtensionTrainingMyCourses',
  )
  const unpaidText = useSelector((store: any) => {
    return store.settings.isCustomUnpaidCourseStatusText === '1'
      ? store.settings.custom.customUnpaidCourseStatusText
      : null
  })

  const userRole = useSelector(selectProfile).role as string
  const [addToFavoriteLoading, setAddToFavoriteLoading] = useState(false)
  const [modal, setModal] = useState<ModalConfig | null>(null)
  const course = order.direction

  const modalConfig: Record<string, ModalConfig> = {
    group: {
      type: 'group',
      data: order,
    },
    private: {
      type: 'private',
      data: order,
    },
    buy: {
      type: 'buy',
      data: order,
    },
  }

  const getIcon = (course: ICourse): string | undefined => {
    const path = course?.mediaIcon?.path ?? course?.icon_path ?? undefined
    if (!path) return path
    if (path?.startsWith('blob')) {
      return path
    }
    return process.env.REACT_APP_PATH_FILE + path
  }

  const { isExpired, isFree, dateTitle, dateString, onPaymentClick } =
    useMemo(() => {
      const userClass = order.class?.classUser?.find(
        (item) => item?.user_id === order.children_id,
      )

      const isExpired = order.class
        ? !userClass?.status_payment
        : !order?.status_payment

      const amount = Number(userClass?.amount ?? -1)
      const isFree = order.class ? amount === 0 : order.tariff?.type === 'free'

      let dateTitle: string | null | undefined
      if (!isFree) {
        if (order.class || order.tariff?.type === 'month') {
          dateTitle = isExpired ? 'не оплачено до:' : 'оплачено до:'
        } else if (order.tariff?.type === 'stream_one_time') {
          dateTitle = null
        } else {
          dateTitle = isExpired ? 'не оплачено:' : 'оплачено:'
        }
      }

      let dateString: string | null = null
      if (isFree) {
        dateString = 'Бесплатный'
      } else if (order.class) {
        const date = new Date(userClass?.next_payment_date ?? new Date())
        dateString = formateDate(date, 'DD.MM.YYYY')
      } else if (order.tariff?.type === 'month') {
        const date = new Date(
          order?.next_payment_date ?? new Date(order?.created_at + 1000),
        )
        dateString = formateDate(date, 'DD.MM.YYYY')
      } else if (order.tariff?.type === 'stream_one_time') {
        dateString = isExpired ? unpaidText ?? 'не оплачено' : 'оплачено'
      } else if (order.tariff?.type === 'hour') {
        const hours = Math.round(order.buy_hours!! * 100) / 100
        dateString = `${hours} ${declOfNum(hours, ['час', 'часа', 'часов'])}`
      } else if (order.tariff?.type === 'minute') {
        const minutes = Math.round(order.buy_hours!! * 100) / 100
        dateString = `${minutes} минут`
      }
      if (order.tariff?.type === 'stream_custom_period') {
        const months = Math.round(order.buy_months!! * 100) / 100
        dateString = `${months} ${declOfNum(months, [
          'месяц',
          'месяца',
          'месяцев',
        ])}`
      }

      const onPaymentClick = () => {
        const modalType = isExpired
          ? 'buy'
          : order?.tariff?.type === 'month'
          ? 'group'
          : 'private'
        setModal(modalConfig[modalType] ?? null)
      }

      return { isExpired, isFree, dateTitle, dateString, onPaymentClick }
    }, [order])

  const onFavoriteClick = async () => {
    if (addToFavoriteLoading) return
    setAddToFavoriteLoading(true)
    const res = await addCourseToFavorite(order?.direction_id)
    if (res.success) {
      onAddToFavorite?.(order)

      const successMessage =
        order.favorite === 1
          ? 'Курс убран из избранного!'
          : 'Курс добавлен в избранное!'

      addAlert({ title: successMessage })
    } else {
      addAlert({
        type: 'error',
        title: 'Возникла ошибка при добавлении курса в избранное!',
      })
    }
    setAddToFavoriteLoading(false)
  }

  const linkCourse =
    userRole === 'children'
      ? !!order?.access_deny
        ? '/payment'
        : order?.group_id
        ? `/v2/courses/my/course-or-group-${order?.group_id}`
        : `/v2/courses/my/course-or-group-${order.direction_id}-private`
      : `/courses-check/${order.direction_id}`

  const closeModal = () => setModal(null)

  const isExceptionable = (): boolean => {
    if (isDisabledExtensionTraining) return false
    if (!isExpired && order.tariff?.type === 'stream_one_time') return false
    if (!isFree) return true
    return false
  }

  return (
    <>
      <div className={cn.cardWrapper}>
        <div className={cn.cardHeader}>
          <div className={cn.nameRow}>{course?.name ?? ''}</div>
          {userRole === 'children' &&
            onAddToFavorite &&
            (order?.favorite === 1 ? (
              <RoundedStarFilled
                className={cn.cardStar}
                onClick={onFavoriteClick}
              />
            ) : (
              <RoundedStarOutlined
                className={cn.cardStar}
                onClick={onFavoriteClick}
              />
            ))}
        </div>
        <div className={cn.content}>
          <div className={cn.iconRow}>
            <div className={cn.dateWrapper}>
              {userRole === 'children' && (
                <>
                  {dateTitle && <div className={cn.dateTitle}>{dateTitle}</div>}
                  {dateString && (
                    <div
                      className={classNames(
                        cn.dateString,
                        isExpired && cn.alertTextColor,
                      )}
                    >
                      {dateString}
                    </div>
                  )}
                </>
              )}
            </div>
            <img className={cn.icon} src={getIcon(course)} />
          </div>
        </div>
        <div className={cn.actions}>
          {userRole !== 'children' && (
            <Link
              to={linkCourse}
              className={classNames(cn.button, cn.primaryButton)}
            >
              Перейти
            </Link>
          )}
          {userRole === 'children' && (
            <>
              {!isExpired && (
                <Link to={linkCourse} className={cn.button}>
                  Перейти
                </Link>
              )}
              {isExceptionable() && (
                <TooltipWrapper
                  title={
                    order?.class &&
                    'Этот курс входит в класс, если вы оплатите его, то продлятся все курсы, входящие в этот класс'
                  }
                  style={{ width: '100%' }}
                >
                  <button
                    className={classNames(
                      cn.button,
                      isExpired ? cn.extendButton : cn.primaryButton,
                    )}
                    onClick={onPaymentClick}
                  >
                    {!isExpired ? 'Продлить обучение' : 'Оплатить обучение'}
                  </button>
                </TooltipWrapper>
              )}
            </>
          )}
        </div>
      </div>
      {/* @ts-ignore */}
      <Modal active={!!modal} setActive={closeModal} className={cn.modal}>
        {/* @ts-ignore */}
        <ModalBody {...{ ...modal, setModal, closeModal }} />
      </Modal>
    </>
  )
}

export default MyCourseCard
